import {
  Alert,
  AlertTrigger,
  schAloftArea,
  schSiteRating,
  schSixTierQuality,
} from '@aloft/toolkit-shared';

import { z } from 'zod';

import { PropertyGridCellKey } from '../../../../components/PropertyGrid/types.ts';
import {
  CompPropertyDataState,
  PropertyDataState,
  SubjectPropertyDataState,
} from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';

const ALERT_TRIGGERS: AlertTrigger[] = [
  'navigate/market',
  'navigate/cost-approach',
  'navigate/adjustments',
  'navigate/commentary',
  'navigate/reconciliation',
];

const schSalesComparablesImprovementFields = z.object({
  gla: schAloftArea,
  bedrooms: z.number().int().nonnegative(),
  fullBaths: z.number().int().nonnegative(),
  halfBaths: z.number().int().nonnegative(),
  age: z.number().int().nonnegative(),
  uadQuality: schSixTierQuality,
  uadCondition: schSixTierQuality,
});

const schSalesComparablesFields = z.object({
  id: z.string(),
  site: z.object({
    area: schAloftArea,
    influence: z.string(),
    influenceRating: schSiteRating,
    view: z.string(),
    viewRating: schSiteRating,
  }),
  improvements: schSalesComparablesImprovementFields.extend({
    arv: schSalesComparablesImprovementFields.optional(),
  }),
});

const mapPropertyDataEntityToSalesComparableFieldsSchema = (
  propertyDataEntity: PropertyDataState,
) => {
  if (propertyDataEntity.id === 'subject') {
    const { editableData } = (propertyDataEntity as SubjectPropertyDataState).subjectData;
    const arvImprovements = editableData.arv
      ? {
          gla: editableData.arv?.improvements?.gla,
          bedrooms: editableData.arv?.improvements?.bedrooms,
          fullBaths: editableData.arv?.improvements?.fullBaths,
          halfBaths: editableData.arv?.improvements?.halfBaths,
          age: editableData.improvements?.age,
          uadQuality: editableData.arv?.improvements?.uadQuality,
          uadCondition: editableData.arv?.improvements?.uadCondition,
        }
      : undefined;
    return schSalesComparablesFields.safeParse({
      id: propertyDataEntity.id,
      improvements: {
        gla: editableData.improvements?.gla,
        bedrooms: editableData.improvements?.bedrooms,
        fullBaths: editableData.improvements?.fullBaths,
        halfBaths: editableData.improvements?.halfBaths,
        age: editableData.improvements?.age,
        uadQuality: editableData.improvements?.uadQuality,
        uadCondition: editableData.improvements?.uadCondition,
        arv: arvImprovements,
      },
      site: {
        area: editableData.site?.area,
        influence: editableData.site?.influence,
        influenceRating: editableData.site?.influenceRating,
        view: editableData.site?.view,
        viewRating: editableData.site?.viewRating,
      },
    });
  }
  const { editableData } = (propertyDataEntity as CompPropertyDataState).compData;

  return schSalesComparablesFields.safeParse({
    id: propertyDataEntity.id,
    improvements: {
      gla: editableData.improvements?.gla,
      bedrooms: editableData.improvements?.bedrooms,
      fullBaths: editableData.improvements?.fullBaths,
      halfBaths: editableData.improvements?.halfBaths,
      age: editableData.improvements?.age,
      uadQuality: editableData.improvements?.uadQuality,
      uadCondition: editableData.improvements?.uadCondition,
    },
    site: {
      area: editableData.site?.area,
      influence: editableData.site?.influence,
      influenceRating: editableData.site?.influenceRating,
      view: editableData.site?.view,
      viewRating: editableData.site?.viewRating,
    },
  });
};

export const missingPropertyData: AlertDefinition = ({
  propertyDataEntities,
  compSequence,
  propertyType,
  compType,
  fullCompSequence,
}) => {
  const errors = ['subject', ...compSequence]
    .map((id) => {
      if (propertyType && id === 'subject' && propertyType !== 'subject') {
        return undefined;
      }
      if (propertyType && id !== 'subject' && propertyType !== 'comp') {
        return undefined;
      }
      const propertyDataEntity = propertyDataEntities[id];
      if (!propertyDataEntity) {
        return undefined;
      }

      const result = mapPropertyDataEntityToSalesComparableFieldsSchema(propertyDataEntity);
      if (result.success) {
        return undefined;
      }

      const propertyName =
        id === 'subject' ? 'Subject' : `Comp ${(fullCompSequence?.indexOf(id) ?? 0) + 1}`;

      const firstIssuePath = result.error.issues[0]?.path as (string | number)[] | undefined;
      const targetCellKeyStr: string | undefined = firstIssuePath?.length
        ? `${id}-editableData.${firstIssuePath.join('.')}`
        : undefined;
      const targetCellKey = targetCellKeyStr as PropertyGridCellKey | undefined;

      return {
        type: 'missingPropertyData',
        status: 'active',
        severity: 'error',
        orderType: ['appraisify'],
        message: `${propertyName} has missing data.`,
        compType,
        fixingInfo: {
          tips: 'One or more of the rows for the property is incomplete.',
          pairedCostRouteItem: 'features',
          fixingMethod: ['action'],
          commentAlwaysRequired: false,
          targetCellKey,
        },
        triggers: ALERT_TRIGGERS,
      } as Alert;
    })
    .filter(Boolean);

  return errors[0];
};
